import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import MovieCard from '@/components/feature/MovieCard';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getMoviesByCategory } from '@/mocks/movies';

export default function CategoryPage() {
  const { slug = 'kehuan' } = useParams<{ slug: string }>();
  const currentCategory = getCategoryBySlug(slug);
  const movies = getMoviesByCategory(currentCategory?.name || '');
  const [sortBy, setSortBy] = useState<'rating' | 'year'>('rating');

  const sortedMovies = [...movies].sort((a, b) => {
    if (sortBy === 'rating') return b.rating - a.rating;
    return b.year - a.year;
  });

  const categoryName = currentCategory?.name || slug;

  return (
    <>
      <SEOHead
        title={`${categoryName}电影免费在线观看-高清全集 | 光影在线`}
        description={`免费在线观看${categoryName}电影高清全集，海量${categoryName}影片资源，无需注册即可播放。${currentCategory?.description || ''}`}
        keywords={`${categoryName}电影,${categoryName}电影在线观看,免费${categoryName}电影,${categoryName}电影高清,${categoryName}电影全集`}
        canonicalUrl={`https://guangyingzx.com/category/${slug}`}
        schemaJson={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${categoryName}电影 - 光影在线`,
          description: `免费在线观看${categoryName}电影高清全集`,
          url: `https://guangyingzx.com/category/${slug}`,
        }}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-20">
        <div className="px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap">
              <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-900 font-medium whitespace-nowrap">分类浏览</span>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-primary-600 font-medium whitespace-nowrap">{categoryName}</span>
            </nav>

            <div className="flex flex-col lg:flex-row gap-8">
              <aside className="lg:w-56 shrink-0">
                <h3 className="font-heading font-semibold text-sm text-foreground-900 mb-4">全部分类</h3>
                <div className="flex lg:flex-col gap-1.5 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        slug === cat.slug
                          ? 'bg-primary-500 text-background-50 font-medium'
                          : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                      }`}
                    >
                      <i className={`${cat.icon} text-base`}></i>
                      <span>{cat.name}</span>
                      <span className="text-xs opacity-70 ml-auto">{cat.count}</span>
                    </Link>
                  ))}
                </div>
              </aside>

              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                  <div>
                    <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                      {categoryName}电影
                    </h1>
                    <p className="text-sm text-foreground-500 mt-1">
                      {currentCategory?.description} · 共{movies.length}部影片
                    </p>
                  </div>

                  <div className="flex items-center bg-background-100 rounded-full px-1 py-1">
                    <button
                      onClick={() => setSortBy('rating')}
                      className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        sortBy === 'rating'
                          ? 'bg-primary-500 text-background-50'
                          : 'text-foreground-600 hover:text-foreground-900'
                      }`}
                    >
                      按评分
                    </button>
                    <button
                      onClick={() => setSortBy('year')}
                      className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        sortBy === 'year'
                          ? 'bg-primary-500 text-background-50'
                          : 'text-foreground-600 hover:text-foreground-900'
                      }`}
                    >
                      按年份
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-product-shop>
                  {sortedMovies.map((movie) => (
                    <MovieCard
                      key={movie.id}
                      id={movie.id}
                      title={movie.title}
                      titleEn={movie.titleEn}
                      category={movie.category}
                      year={movie.year}
                      rating={movie.rating}
                      poster={movie.poster}
                      slug={movie.slug}
                    />
                  ))}
                </div>

                {movies.length === 0 && (
                  <div className="text-center py-20">
                    <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                      <i className="ri-film-line text-2xl text-foreground-400"></i>
                    </div>
                    <p className="text-foreground-500">该分类暂无影片，请浏览其他分类</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}